import { Injectable, NgZone } from "@angular/core";
import { Observable, ReplaySubject, forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { CentrosService } from "./centros.service";
import { ContractService } from "src/app/modules/contracts/services/contract.service";
import { OpportunityService } from "src/app/modules/opportunities/services/opportunity.service";
import { CebeService } from "src/app/modules/cebes/services/cebe.service";
import {
	SearchEntry,
	SearchEntryKind,
} from "src/app/_core/models/main/search-entry.model";
import { Policies } from "src/app/_core/models/main/policies.enum";
import { UserProfileStore } from "src/app/_core/stores/user-profile.store";

export interface PaletteGroup {
	kind: SearchEntryKind;
	label: string;
	entries: SearchEntry[];
}

// Orden y metadatos de presentación por tipo.
const KIND_ORDER: SearchEntryKind[] = ["contract", "opportunity", "centro", "cebe"];
const KIND_LABEL: Record<SearchEntryKind, string> = {
	contract: "commandPalette.group.contracts",
	opportunity: "commandPalette.group.opportunities",
	centro: "commandPalette.group.centros",
	cebe: "commandPalette.group.cebes",
};

/**
 * Índice de búsqueda global para el Command Palette (Ctrl/Cmd+K).
 *
 * - Carga los tres listados (Contratos, Oportunidades, Centros) con los MISMOS
 *   métodos de backend que usan sus list-views, de modo que respeta los permisos
 *   del usuario y los datos son completos/navegables.
 * - La carga se dispara en segundo plano (requestIdleCallback) y NO bloquea el
 *   renderizado de ninguna pantalla; el resultado se cachea para la sesión.
 * - `ready$` (ReplaySubject(1)) avisa cuando el índice está listo, incluso si el
 *   diálogo se abre después de la precarga.
 */
@Injectable({ providedIn: "root" })
export class GlobalSearchService {
	private readonly _ready$ = new ReplaySubject<void>(1);
	readonly ready$: Observable<void> = this._ready$.asObservable();

	private snapshot: SearchEntry[] = [];
	private started = false;

	constructor(
		private readonly zone: NgZone,
		private readonly centrosService: CentrosService,
		private readonly contractService: ContractService,
		private readonly opportunityService: OpportunityService,
		private readonly cebeService: CebeService,
		private readonly userProfileStore: UserProfileStore,
	) {}

	/**
	 * ¿El usuario puede VER esta entidad? Misma lógica que `hasAccess` del header:
	 * Admin ve todo; el resto necesita la policy `View` correspondiente. Así la
	 * precarga no llama a endpoints que devolverían 403 (p.ej. Oport/Cebe para USUARIO),
	 * evitando los toasts de error en la carga.
	 */
	private canSee(policy: string): boolean {
		const userPolicies =
			this.userProfileStore.getUserProfile()?.profiles?.map((up) => up.profile) ?? [];
		return (
			userPolicies.indexOf(Policies.Admin) > -1 || userPolicies.indexOf(policy) > -1
		);
	}

	/** Dispara la precarga una sola vez, en idle, sin bloquear la carga de pantalla. */
	prefetch(): void {
		if (this.started) {
			return;
		}
		this.started = true;
		const run = () => this.load();
		const ric = (window as unknown as {
			requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
		}).requestIdleCallback;
		if (typeof ric === "function") {
			ric(run, { timeout: 4000 });
		} else {
			setTimeout(run, 1500);
		}
	}

	private load(): void {
		forkJoin({
			contracts: this.canSee(Policies.Contrato_View)
				? this.contractService
						.getContractsList()
						.pipe(catchError(() => of([] as unknown[])))
				: of([] as unknown[]),
			opportunities: this.canSee(Policies.Oportunidad_View)
				? this.opportunityService
						.getOpportunitiesList()
						.pipe(catchError(() => of([] as unknown[])))
				: of([] as unknown[]),
			centros: this.canSee(Policies.Centro_View)
				? this.centrosService
						.getCentros()
						.pipe(catchError(() => of([] as unknown[])))
				: of([] as unknown[]),
			cebes: this.canSee(Policies.Cebe_View)
				? this.cebeService
						.getCebesList()
						.pipe(catchError(() => of([] as unknown[])))
				: of([] as unknown[]),
		}).subscribe(({ contracts, opportunities, centros, cebes }) => {
			const entries: SearchEntry[] = [];
			for (const c of (contracts ?? []) as any[]) {
				const e = this.mapContract(c);
				if (e) {
					entries.push(e);
				}
			}
			for (const o of (opportunities ?? []) as any[]) {
				const e = this.mapOpportunity(o);
				if (e) {
					entries.push(e);
				}
			}
			for (const c of (centros ?? []) as any[]) {
				const e = this.mapCentro(c);
				if (e) {
					entries.push(e);
				}
			}
			for (const c of (cebes ?? []) as any[]) {
				const e = this.mapCebe(c);
				if (e) {
					entries.push(e);
				}
			}
			this.snapshot = entries;
			this.zone.run(() => this._ready$.next());
		});
	}

	// ── Mappers (defensivos con los campos del modelo de cada listado) ──
	private mapContract(c: any): SearchEntry | null {
		if (c?.id == null) {
			return null;
		}
		const code = (c.codigo ?? "").toString().trim();
		const desc = (c.descripcion ?? "").toString().trim();
		const cliente = (c.datosContrato?.cliente ?? c.sociedad ?? "")
			.toString()
			.trim();
		const pais = (c.descripcionPais ?? "").toString().trim();
		const title = code || desc || "—";
		const subtitle = code ? desc || cliente : cliente;
		return {
			id: `contract-${c.id}`,
			kind: "contract",
			title,
			subtitle: subtitle || undefined,
			badge: "commandPalette.kind.contract",
			icon: "description",
			tone: "red",
			url: `/contracts/${c.id}`,
			haystack: `${code} ${desc} ${cliente} ${pais}`.toLowerCase(),
		};
	}

	private mapOpportunity(o: any): SearchEntry | null {
		if (o?.id == null) {
			return null;
		}
		const code = (o.codigoOportunidad ?? "").toString().trim();
		const nombre = (o.nombre ?? "").toString().trim();
		const sociedad = (o.sociedad ?? o.nombreCuenta ?? "").toString().trim();
		const pais = (o.pais ?? "").toString().trim();
		const actividad = (o.actividad ?? "").toString().trim();
		const title = code || nombre || "—";
		const subtitle = code ? nombre || sociedad : sociedad;
		return {
			id: `opportunity-${o.id}`,
			kind: "opportunity",
			title,
			subtitle: subtitle || undefined,
			badge: "commandPalette.kind.opportunity",
			icon: "lightbulb",
			tone: "amber",
			url: `/opportunities/${o.id}`,
			haystack:
				`${code} ${nombre} ${sociedad} ${pais} ${actividad}`.toLowerCase(),
		};
	}

	private mapCentro(c: any): SearchEntry | null {
		if (c?.id == null) {
			return null;
		}
		const code = (c.codigo ?? "").toString().trim();
		const desc = (c.descripcion ?? "").toString().trim();
		const ciudad = (c.ciudad ?? "").toString().trim();
		const provincia = (c.provincia ?? "").toString().trim();
		const tipo = (c.tipo_Instalacion ?? "").toString().trim();
		const title = code || desc || "—";
		const loc = [ciudad, provincia].filter(Boolean).join(", ");
		const subtitle = code ? desc || loc : loc;
		return {
			id: `centro-${c.id}`,
			kind: "centro",
			title,
			subtitle: subtitle || undefined,
			badge: "commandPalette.kind.centro",
			icon: "place",
			tone: "blue",
			url: `/sites/${c.id}`,
			haystack:
				`${code} ${desc} ${ciudad} ${provincia} ${tipo}`.toLowerCase(),
		};
	}

	private mapCebe(c: any): SearchEntry | null {
		if (c?.id == null) {
			return null;
		}
		const code = (c.codigo ?? "").toString().trim();
		const desc = (c.descripcion ?? "").toString().trim();
		const pais = (c.paisDescripcion ?? "").toString().trim();
		const estado = (c.estado ?? "").toString().trim();
		const title = code || desc || "—";
		const subtitle = code ? desc || pais : pais;
		return {
			id: `cebe-${c.id}`,
			kind: "cebe",
			title,
			subtitle: subtitle || undefined,
			badge: "commandPalette.kind.cebe",
			icon: "business",
			tone: "green",
			url: `/profit-centers/${c.id}`,
			haystack: `${code} ${desc} ${pais} ${estado}`.toLowerCase(),
		};
	}

	// ── Búsqueda ──────────────────────────────────────────────────────
	search(query: string, limit = 20): SearchEntry[] {
		const q = (query ?? "").trim().toLowerCase();
		if (!q) {
			return this.takeHead(Math.min(limit, 9));
		}
		if (!this.snapshot.length) {
			return [];
		}
		const scored: Array<{ entry: SearchEntry; score: number }> = [];
		for (const entry of this.snapshot) {
			const score = this.score(entry, q);
			if (score > 0) {
				scored.push({ entry, score });
			}
		}
		scored.sort((a, b) => b.score - a.score);
		return scored.slice(0, limit).map((s) => s.entry);
	}

	private score(entry: SearchEntry, q: string): number {
		const title = entry.title.toLowerCase();
		if (title === q) {
			return 1000;
		}
		if (title.startsWith(q)) {
			return 800;
		}
		if (title.split(/[\s\-_/]+/).some((w) => w.startsWith(q))) {
			return 600;
		}
		if (title.includes(q)) {
			return 400;
		}
		if (entry.subtitle && entry.subtitle.toLowerCase().includes(q)) {
			return 250;
		}
		if (entry.haystack.includes(q)) {
			return 200;
		}
		return 0;
	}

	/**
	 * Cabecera equilibrada para query vacía: muestra los primeros de CADA tipo
	 * (incl. CEBEs). Reparte por defecto a partes iguales (floor) para que todos
	 * los tipos aparezcan en el preview en vez de llenarse con los primeros.
	 */
	private takeHead(limit: number): SearchEntry[] {
		const perKind = Math.max(1, Math.floor(limit / KIND_ORDER.length));
		const out: SearchEntry[] = [];
		for (const kind of KIND_ORDER) {
			out.push(
				...this.snapshot.filter((e) => e.kind === kind).slice(0, perKind),
			);
		}
		return out;
	}

	/** Agrupa por tipo en orden fijo, conservando el orden de relevancia interno. */
	groupByKind(entries: SearchEntry[]): PaletteGroup[] {
		const groups: PaletteGroup[] = [];
		for (const kind of KIND_ORDER) {
			const list = entries.filter((e) => e.kind === kind);
			if (list.length) {
				groups.push({ kind, label: KIND_LABEL[kind], entries: list });
			}
		}
		return groups;
	}
}
